// src/utils/notesFeed.js
// Feed-style submission notes. Each post is attributed to an account and stamped
// with a created (and optionally edited) time. Stored inside submission.analysis
// as `noteEntries`; see DatabaseService for persistence.

function parseAnalysis(analysis) {
  if (!analysis) return null;
  if (typeof analysis === 'string') {
    try { return JSON.parse(analysis); } catch (e) { return null; }
  }
  return analysis;
}

// Build the noteEntries array for a submission, migrating a legacy single-string
// note into one "legacy" post when no feed exists yet.
export function migrateNotes(analysis) {
  const parsed = parseAnalysis(analysis);
  if (parsed && Array.isArray(parsed.noteEntries)) {
    return parsed.noteEntries;
  }
  const legacy = (parsed && typeof parsed.notes === 'string' ? parsed.notes : '').trim();
  if (legacy) {
    return [{ id: 'legacy', authorId: '', authorName: '', text: legacy, createdAt: null, editedAt: null }];
  }
  return [];
}

// Prepend a new post. Blank text is a no-op (returns the same array reference).
export function addNoteEntry(entries, { id, text, author, createdAt }) {
  if (!text || !text.trim()) return entries;
  const entry = {
    id,
    authorId: author?.id || '',
    authorName: author?.name || '',
    text: text.trim(),
    createdAt,
    editedAt: null,
  };
  return [entry, ...entries];
}

export function editNoteEntry(entries, id, text, editedAt) {
  return entries.map((e) => (e.id === id ? { ...e, text: text.trim(), editedAt } : e));
}

export function deleteNoteEntry(entries, id) {
  return entries.filter((e) => e.id !== id);
}

export function latestNoteText(entries) {
  return entries && entries.length ? (entries[0].text || '') : '';
}

export function formatNoteTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function authorLabel(entry) {
  return (entry && entry.authorName) ? entry.authorName : '';
}

// Compact "author · date · bearbeitet" caption, omitting any part we don't
// have. Legacy notes predate author/timestamp logging, so for them this
// returns '' (or just the marker) instead of placeholder dashes.
export function noteByline(entry) {
  const author = (entry && entry.authorName) ? entry.authorName.trim() : '';
  const t = formatNoteTimestamp(entry && entry.createdAt);
  const time = (t && t !== '—') ? t : '';
  const parts = [author, time].filter(Boolean);
  if (entry && entry.editedAt) parts.push('bearbeitet');
  return parts.join(' · ');
}
